import React, {  useCallback, useMemo, useRef}from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import { AgGridReact as AgGridReactType } from 'ag-grid-react';
import { type ColDef, themeAlpine } from 'ag-grid-community';
import '@process-advisor/process-miner/shared/gridStyles.css'
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import ClusterIcon from '@process-advisor/assets/clusterIcon.png'
import InfluencerIcon from '@process-advisor/assets/influencerIcon.png'
import VisualizerIcon from '@process-advisor/assets/processMinerIcon.png'
import AnalyticsIcon  from '@process-advisor/assets/analyticsIcon.png'
import DecomposeIcon  from '@process-advisor/assets/decomposeIcon.png'
import ResetIcon  from '@process-advisor/assets/resetIcon.png'
import TaskLifecycleIcon  from '@process-advisor/assets/taskLifecycleIcon.png'
import FilterIcon  from '@process-advisor/assets/filterIcon.png'
import { SidebarLoaderContext, type SidebarLoaderContextType } from '@process-advisor/process-miner/shared/providers/sidebarLoaderProvider';

const Wrapper = styled.div`
    height: 750px;
    width: 100%;
    padding: 10px;
    position: relative;
`;

const GridBusyOverlay = styled.div`
    position: absolute;
    top: 54px;
    right: 24px;
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #B3D4FF;
    border-radius: 8px;
    background: rgba(244, 248, 255, 0.96);
    color: #172B4D;
    font-size: 12px;
    font-weight: 600;
    box-shadow: 0 6px 18px rgba(9, 30, 66, 0.16);
    pointer-events: none;
`;

const GridBusySpinner = styled.span`
    width: 16px;
    height: 16px;
    border: 2px solid #B3D4FF;
    border-top-color: #0C66E4;
    border-radius: 50%;
    animation: pa-grid-spin 0.8s linear infinite;

    @keyframes pa-grid-spin {
        to { transform: rotate(360deg); }
    }
`;

type SingleRow = {
    id: number;
    [key: string]: any
}

type Props = {
    rows: SingleRow[];
    columns: ColDef[];
    type: string,
}
let activityColumns = []

let groupedColumns = 0
let data 
export const DataGrid: React.FC<Props> = ({
    rows,
    columns,
    type,
}) => {
    const { setProps } = React.useContext<SidebarLoaderContextType>(SidebarLoaderContext);
    const gridRef  = useRef<AgGridReactType>(null);
    const [filterApplied, setFilterApplied]  = React.useState<boolean>(false);
    const [filterMenuName, setFilterMenuName]  = React.useState<string>('Filter similar ' + apiProvider.getEntityLower() + 's');
    const [gridBusy, setGridBusy] = React.useState<boolean>(false);
    const [gridBusyMessage, setGridBusyMessage] = React.useState<string>('Updating activity time grid...');
    const gridBusyTimerRef = React.useRef<any>(null);
    const taskTable = apiProvider.getTaskTable();
    var urlHost = window.location.protocol + '//' + window.location.host
    
    //const [groupedColumns, setGroupedColumns] = React.useState<number>(0);

    React.useEffect(() => {
        return () => {
            if (gridBusyTimerRef.current) clearTimeout(gridBusyTimerRef.current);
        };
    }, []);

    const getGridApi = React.useCallback((params?: any) => params?.api || gridRef.current?.api, []);

    const hideGridBusy = React.useCallback((params?: any, delay = 250) => {
        const api = getGridApi(params);
        if (gridBusyTimerRef.current) clearTimeout(gridBusyTimerRef.current);
        gridBusyTimerRef.current = setTimeout(() => {
            try { api?.hideOverlay?.(); } catch (_) {}
            setGridBusy(false);
            gridBusyTimerRef.current = null;
        }, delay);
    }, [getGridApi]);

    const showGridBusy = React.useCallback((message = 'Updating activity time grid...', params?: any) => {
        const api = getGridApi(params);
        setGridBusyMessage(message);
        setGridBusy(true);
        try {
            if (typeof requestAnimationFrame === 'function') {
                requestAnimationFrame(() => api?.showLoadingOverlay?.());
            } else {
                setTimeout(() => api?.showLoadingOverlay?.(), 0);
            }
        } catch (_) {}

        if (gridBusyTimerRef.current) clearTimeout(gridBusyTimerRef.current);
        gridBusyTimerRef.current = setTimeout(() => {
            try { api?.hideOverlay?.(); } catch (_) {}
            setGridBusy(false);
            gridBusyTimerRef.current = null;
        }, 3000);
    }, [getGridApi]);

    function stdev(params) {
    
    let arr = [];
        params.values.forEach((value) => {
            if (typeof value === 'number') {
                arr.push(value);
            }
        });
        
        let mean = arr.reduce((acc, curr)=>{
            return acc + curr
        }, 0) / arr.length;
        
        arr = arr.map((k)=>{
            return (k - mean) ** 2
        });
        
        let sum = arr.reduce((acc, curr)=> acc + curr, 0);
        let variance = sum / (arr.length - 1);
        return Math.sqrt(variance);
    }
    

    React.useEffect(() => {
        data = rows
        let tempColumns = apiProvider.getTimeColumns();
        let tempActivityColumns = [] 
        for (let j = 0; j < tempColumns.length; j++) {
            if (tempColumns[j].field.startsWith('^^^') === true) tempActivityColumns.push(tempColumns[j].field.replace('^^^','') )
        }
        activityColumns = tempActivityColumns
    }, [rows]);

    const onFirstDataRendered  = (params) => {
        
        const allColumnIds = [];
        params.api.getColumns().forEach((column) => {
          allColumnIds.push(column.getId());
        });
        params.api.autoSizeColumns(allColumnIds, false);
        };

    const onSaveGridColumnState = (params) => {
        let columnState = params.api.getColumnState();
        apiProvider.saveAAGridState({columnState})
    }

    const onGridSortChanged = (params) => {
        showGridBusy('Sorting activity time grid...', params);
        onSaveGridColumnState(params);
        hideGridBusy(params, 350);
    }

    const onGridFilterChanged = (params) => {
        showGridBusy('Filtering activity time grid...', params);
        onSaveGridColumnState(params);
        hideGridBusy(params, 350);
    }

    const onGridModelUpdated = (params) => {
        hideGridBusy(params, 150);
    }

    const onGridColumnHeaderClicked = (params) => {
        // This fires before the sort model is applied, so the browser has a chance
        // to paint the loading overlay before a large Activity Time grid recalculates.
        showGridBusy('Sorting activity time grid...', params);
    }

    const onGridFilterOpened = (params) => {
        showGridBusy('Preparing activity time filter...', params);
    }

    const onGridFilterModified = (params) => {
        showGridBusy('Filtering activity time grid...', params);
    }

    const columnRowGroupChanged   = (params) => {
        showGridBusy('Updating activity time grouping...', params);
        //setGroupedColumns(params.columns.length)
        groupedColumns = params.columns.length
        hideGridBusy(params, 350);
    }

    const statusBar = useMemo(() => {
        return {
          statusPanels: [
            { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
            { statusPanel: 'agTotalRowCountComponent', align: 'center' },
          ],
        };
      }, []);


    function variantFilter(variant) {
        var variantFilterComponent = gridRef.current.api.getFilterInstance('Variant')
        variantFilterComponent.setModel({
            type: 'equals',
            filterType: 'number',
            filter: variant,
        });
        gridRef.current.api.onFilterChanged();
        setFilterMenuName('Reset filters')
        setFilterApplied(true)
    }


    function resetFilters() {
        gridRef.current.api.setFilterModel(null)
    }


    const getContextMenuItems = useCallback((params) => {

        let activityColumn = false
        for (let j = 0; j < activityColumns.length; j++) {
            if (activityColumns[j] === params.column.colId) {activityColumn = true}
        }
        if (groupedColumns > 0 && activityColumn === true && params.node.leafGroup) {
            let groups = gridGroups(params)
            apiProvider.setSelectedTimeTasks({tasks:data,groups: groups})

            let activityID = -1
            let activityArray = apiProvider.getActivityArray()
            for (let j = 0; j < activityArray.length; j++) {
                if (activityArray[j].activity === params.column.colId) activityID = activityArray[j].id
            }   
            apiProvider.setTimeActivity({activity:activityID})
            let menu = [
                {
                    name: 'Insights',
                    icon: '<img src="' + AnalyticsIcon + '" />',
                    subMenu: [
                        {
                            name: 'Clusters',
                            action: function () {
                                apiProvider.setDiagramCluster({source: 'tia'})
                            },
                            icon: '<img src="' + ClusterIcon + '" />',
                        },          
                        {
                            name: 'Influencers',
                            action: function () {
                                apiProvider.setDiagramInfluenceAnalysis({source: 'tia'})
                            },
                            icon: '<img src="' + InfluencerIcon + '" />',
                        },    
                        {
                            name: 'Decompose',
                            action: function () {
                                apiProvider.setDiagramDecompositionTree({source: 'tia' })
                            },
                            icon: '<img src="' + DecomposeIcon + '" />',
                        },
                    ],
                },
                'separator',
                'copy',
                'separator',
                'csvExport',
                'excelExport'
            
            ]
    
            return menu
        }

        if (params.column.colId === apiProvider.getEntityUpper() + ' #' && params.node.id.startsWith('row-group-') === false ) {
            let id = params.node.data['ID']
            let issue = params.node.data[apiProvider.getEntityUpper() + ' #']
            let variant = params.node.data['Variant']
            let selectedData = []
            let vtp = {}
            vtp['id'] = id
            vtp['k'] = issue
            selectedData.push(vtp)
            apiProvider.setTaskDetailSelectedData({ selection: selectedData });
            return [
                {
                    // custom item
                    name: apiProvider.getEntityUpper() + ' LifeCycle',
                    action: function () {
                        apiProvider.setTaskID({taskID: issue })
                        apiProvider.openLifecycleModalSingle({props: issue})
                    },
                    icon: '<img src="' + TaskLifecycleIcon + '" />',
                },
                {
                    // custom item
                    name: 'Process Miner',
                    action: function () {
                        let variants = apiProvider.variantsTable({})
                        let selectedVariant = []
                        for (let j = 0; j < variants.length; j++) {
                            if (variants[j].variant_id === variant) {
                                selectedVariant.push(variants[j])
                                break
                            }
                        }
                        apiProvider.setVariantSlicerSelectedData({ selection: selectedVariant, source: 'time', mode: 'process_miner' });
                        apiProvider.setAction_Visualizer({})
                    },
                    icon: '<img src="' + VisualizerIcon + '" />',
                },
                {
                    // custom item
                    name: filterMenuName,
                    action: function () {
                        variantFilter(variant)
                    },
                    icon: '<img src="' + FilterIcon + '" />',
                },
                {
                    // custom item
                    name: 'Reset filters',
                    action: function () {
                        resetFilters()
                    },
                    icon: '<img src="' + ResetIcon + '" />',
                },
                'separator',
                'copy',
                'separator',
                'csvExport',
                'excelExport'

            ]
        } 

         return [
            'copy',
            'separator',
            'csvExport',
            'excelExport'
        ]
    }, []);    

    function gridGroups(params) {
        let groups = []
        let vtp = {}
        vtp['field'] = params.node.field
        vtp['value'] = params.node.groupData['ag-Grid-AutoColumn']
        groups.push(vtp)
        let parent = params.node.parent

        let parentExists = true
        while (parentExists === true) {
            if (parent.id === 'ROOT_NODE_ID') {parentExists = false} else {
                vtp = {}
                vtp['field'] = parent.field
                vtp['value'] = parent.groupData['ag-Grid-AutoColumn']
                groups.push(vtp)
            }
            parent = parent.parent
        }                   
        return groups
    }

    const enableCharts = true;
    const enableRangeSelection = true;
    const getRowHeight = () => 25;

    return (
        <Wrapper>
            {gridBusy && (
                <GridBusyOverlay>
                    <GridBusySpinner />
                    <span>{gridBusyMessage}</span>
                </GridBusyOverlay>
            )}
            <div
                
                style={{
                    height: '100%',
                    width: '100%',
                    fontSize: '12px',
                    fontFamily: 'system-ui'
,
                }}>
                <AgGridReact theme={themeAlpine} 
                    ref={gridRef}
                    rowSelection="multiple"
                    onFirstDataRendered={onFirstDataRendered}
                    getRowHeight={getRowHeight}
                    overlayNoRowsTemplate={'Please select a source for the detail data'}
                    overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Processing Activity Time grid...</span>'}
                    columnDefs={columns}
                    headerHeight={40}
                    statusBar={statusBar}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        resizable: true,
                        wrapText: true,
                        autoHeight: true,
                    }}
                    rowData={rows}
                    animateRows={true}

                    groupDisplayType={'singleColumn'}
                    groupHideOpenParents={false}
                    rowGroupPanelShow={'always'}  
                    suppressAggFuncInHeader={true}
                    
                    onColumnRowGroupChanged={columnRowGroupChanged}
                    onColumnHeaderClicked={onGridColumnHeaderClicked}
                    onFilterOpened={onGridFilterOpened}
                    onFilterModified={onGridFilterModified}
                    onModelUpdated={onGridModelUpdated}
                    getContextMenuItems={getContextMenuItems}

                    onFilterChanged={onGridFilterChanged}
                    onColumnVisible={onSaveGridColumnState}
                    onColumnResized={onSaveGridColumnState}
                    onSortChanged={onGridSortChanged}
                    onGridColumnsChanged={onSaveGridColumnState}

                    sideBar={
                        {
                            toolPanels: [
                                {
                                    id: 'columns',
                                    labelDefault: 'Columns',
                                    labelKey: 'columns',
                                    iconKey: 'columns',
                                    toolPanel: 'agColumnsToolPanel',
                                    toolPanelParams: {
                                        suppressRowGroups: true,
                                        suppressValues: true,
                                        suppressPivotMode: true, 
                                    }
                      
                                },
                                {
                                    id: 'filters',
                                    labelDefault: 'Filters',
                                    labelKey: 'filters',
                                    iconKey: 'filter',
                                    toolPanel: 'agFiltersToolPanel',
                                },
                            ],
                            hiddenByDefault: false,
                            defaultToolPanel: '',
                        }
                    }
                    enableCharts={enableCharts}
                    chartThemes={[
                        'ag-solar',
                        'ag-pastel',
                        'ag-material-dark',
                        'ag-vivid-dark',
                    ]}
                />
            </div>
        </Wrapper>
    );
};
